import React from 'react';
import { Typography, Button } from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import EmailIcon from '@material-ui/icons/Email';
import LaptopIcon from '@material-ui/icons/Laptop';
import DnsIcon from '@material-ui/icons/Dns';
import CloudIcon from '@material-ui/icons/Cloud';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import { useStyles, CAT_YELLOW } from './utils/styles';

interface HeroSectionProps {
  onScrollToOnboarding: () => void;
}

export default function HeroSection({ onScrollToOnboarding }: HeroSectionProps) {
  const classes = useStyles();
  return (
      <section className={classes.hero}>
        {/* Animated dots */}
        {[
          { w: 320, h: 320, top: '-80px', left: '-80px', delay: '0s' },
          { w: 200, h: 200, top: '60%', right: '-60px', delay: '2s' },
          { w: 140, h: 140, top: '20%', left: '45%', delay: '1s' },
          { w: 80, h: 80, bottom: '15%', left: '30%', delay: '3s' },
        ].map((dot, i) => (
          <div
            key={i}
            className={classes.heroBgDot}
            style={{
              width: dot.w,
              height: dot.h,
              top: (dot as any).top,
              left: (dot as any).left,
              right: (dot as any).right,
              bottom: (dot as any).bottom,
              animationDelay: dot.delay,
            }}
          />
        ))}

        <div className={classes.heroGrid}>
          {/* Left */}
          <div className={classes.heroLeft}>
            <div className={classes.heroBadge}>
              <div className={classes.heroBadgeDot} />
              <Typography className={classes.heroBadgeText}>Helios API Platform</Typography>
            </div>

            <Typography className={classes.heroHeading}>
              Start Using{' '}
              <span className={classes.heroHeadingAccent}>Helios Platform APIs</span>
            </Typography>

            <Typography className={classes.heroDescription}>
              A guided, self-service journey to get your application connected, from client setup to production access.
            </Typography>

            <div className={classes.heroBtnRow}>
              <Button
                className={classes.btnPrimary}
                onClick={onScrollToOnboarding}
                size="large"
              >
                Start Onboarding
              </Button>
              <Button
                className={classes.btnSecondary}
                href="mailto:Service_Adoption_Team_Cosmos@cat.com"
                size="large"
                startIcon={<EmailIcon />}
              >
                Contact Us
              </Button>
            </div>
          </div>

          {/* Right illustration — triangular animated flow */}
          <div className={classes.heroRight}>
            <div style={{
              width: '100%',
              maxWidth: 480,
              background: 'rgba(255,255,255,0.04)',
              border: '1.5px solid rgba(255,255,255,0.12)',
              borderRadius: 24,
              padding: '28px 24px 20px',
              backdropFilter: 'blur(8px)',
            }}>
              <Typography style={{ color: '#9E9E9E', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', textAlign: 'center', marginBottom: 16 }}>
                API Request Flow
              </Typography>

              {/* Fixed-width diagram container so SVG and HTML nodes share coordinates */}
              <div style={{ position: 'relative', width: 380, height: 360, margin: '0 auto' }}>

                {/* ── SVG layer (z:1): paths run edge-to-edge, never entering node circles ── */}
                {/* Node centers: N1(122,276) N2(81,148) N3(190,68) N4(299,148) N5(258,276) */}
                {/* Each path starts/ends ~48px from the node center (radius 44 + 4px gap)   */}
                <svg
                  style={{ position: 'absolute', top: 0, left: 0, width: 380, height: 360, overflow: 'visible', zIndex: 1 }}
                  viewBox="0 0 380 360"
                >
                  {/* Dashed bases — edge to edge */}
                  <path d="M 107,230 C 95,220 88,200 96,194" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeDasharray="5 5" />
                  <path d="M 120,120 C 125,110 138,98 151,96"  fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeDasharray="5 5" />
                  <path d="M 229,96 C 238,100 252,114 260,120" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeDasharray="5 5" />
                  <path d="M 284,194 C 285,207 278,222 273,230" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeDasharray="5 5" />

                  {/* Animated glow paths */}
                  <path d="M 107,230 C 95,220 88,200 96,194" fill="none" stroke={CAT_YELLOW} strokeWidth="3" strokeLinecap="round"
                    style={{ animation: `seqPath1 10s linear infinite`, filter: `drop-shadow(0 0 5px ${CAT_YELLOW})` }} />
                  <path d="M 120,120 C 125,110 138,98 151,96" fill="none" stroke={CAT_YELLOW} strokeWidth="3" strokeLinecap="round"
                    style={{ animation: `seqPath2 10s linear infinite`, filter: `drop-shadow(0 0 5px ${CAT_YELLOW})` }} />
                  <path d="M 229,96 C 238,100 252,114 260,120" fill="none" stroke={CAT_YELLOW} strokeWidth="3" strokeLinecap="round"
                    style={{ animation: `seqPath3 10s linear infinite`, filter: `drop-shadow(0 0 5px ${CAT_YELLOW})` }} />
                  <path d="M 284,194 C 285,207 278,222 273,230" fill="none" stroke={CAT_YELLOW} strokeWidth="3" strokeLinecap="round"
                    style={{ animation: `seqPath4 10s linear infinite`, filter: `drop-shadow(0 0 5px ${CAT_YELLOW})` }} />
                </svg>

                {/* ── Traveling dots (CSS offsetPath, z:1 — synced with seqNode CSS animations) ── */}
                {([
                  { path: 'M 107,230 C 95,220 88,200 96,194',    anim: 'seqDot1' },
                  { path: 'M 120,120 C 125,110 138,98 151,96',   anim: 'seqDot2' },
                  { path: 'M 229,96 C 238,100 252,114 260,120',  anim: 'seqDot3' },
                  { path: 'M 284,194 C 285,207 278,222 273,230', anim: 'seqDot4' },
                ] as { path: string; anim: string }[]).map(({ path, anim }) => (
                  <div key={anim} style={{
                    position: 'absolute', top: 0, left: 0,
                    width: 14, height: 14, borderRadius: '50%',
                    background: CAT_YELLOW,
                    boxShadow: `0 0 12px 4px ${CAT_YELLOW}AA`,
                    offsetPath: `path("${path}")`,
                    animation: `${anim} 10s linear infinite`,
                    zIndex: 1,
                  } as React.CSSProperties} />
                ))}

                {/* ── Nodes — isolated stacking context (z:10) guarantees they paint above dots ── */}
                <div style={{ position: 'absolute', inset: 0, zIndex: 10, isolation: 'isolate' } as React.CSSProperties}>
                {([
                  { left: 78,  top: 232, anim: 'seqNode1', icon: <LaptopIcon     style={{ fontSize: 36, color: CAT_YELLOW }} />, label: 'Your Request', labelLeft: 58,  labelTop: 330 },
                  { left: 37,  top: 104, anim: 'seqNode2', icon: <DnsIcon         style={{ fontSize: 36, color: CAT_YELLOW }} />, label: 'Cosmos',       labelLeft: 16,  labelTop: 202 },
                  { left: 146, top: 24,  anim: 'seqNode3', icon: <VpnKeyIcon      style={{ fontSize: 36, color: CAT_YELLOW }} />, label: 'Entitlements', labelLeft: 130, labelTop: 122 },
                  { left: 255, top: 104, anim: 'seqNode4', icon: <AccountTreeIcon style={{ fontSize: 36, color: CAT_YELLOW }} />, label: 'Apigee',        labelLeft: 245, labelTop: 202 },
                  { left: 214, top: 232, anim: 'seqNode5', icon: <CloudIcon       style={{ fontSize: 36, color: CAT_YELLOW }} />, label: 'Helios API',   labelLeft: 200, labelTop: 330 },
                ] as { left: number; top: number; anim: string; icon: React.ReactNode; label: string; labelLeft: number; labelTop: number }[]).map(({ left, top, anim, icon, label, labelLeft, labelTop }) => (
                  <React.Fragment key={label}>
                    <div style={{
                      position: 'absolute', left, top,
                      width: 88, height: 88, borderRadius: '50%',
                      background: `linear-gradient(135deg, ${CAT_YELLOW}28, ${CAT_YELLOW}06)`,
                      border: `2.5px solid ${CAT_YELLOW}55`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      animation: `${anim} 10s ease-in-out infinite`,
                      transition: 'border-color 0.3s',
                      zIndex: 3,
                    }}>
                      {icon}
                    </div>
                    <Typography style={{ position: 'absolute', left: labelLeft, top: labelTop, width: 108, textAlign: 'center', color: '#BDBDBD', fontSize: 11, fontWeight: 700, letterSpacing: 0.3, zIndex: 3 }}>{label}</Typography>
                  </React.Fragment>
                ))}
                </div>{/* end nodes isolation wrapper */}
              </div>

              {/* Stats */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 20 }}>
                {[
                  { value: '1.3K+', label: 'API Requests / Year' },
                  { value: '5 Steps', label: 'To Go Live' },
                  { value: '3 Envs', label: 'PROD · DEV · QA' },
                ].map(stat => (
                  <div key={stat.label} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    background: `${CAT_YELLOW}12`, border: `1px solid ${CAT_YELLOW}40`,
                    borderRadius: 12, padding: '8px 16px', minWidth: 90,
                  }}>
                    <span style={{ color: CAT_YELLOW, fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>{stat.value}</span>
                    <span style={{ color: '#9E9E9E', fontWeight: 500, fontSize: 10, marginTop: 2, textAlign: 'center' }}>{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
           className={classes.scrollIndicator}
           onClick={onScrollToOnboarding}
           onKeyDown={e => {
             if (e.key === 'Enter' || e.key === ' ') {
               e.preventDefault();
               onScrollToOnboarding();
             }
           }}
           role="button"
           tabIndex={0}
         >
          <Typography className={classes.scrollText}>Explore</Typography>
          <ArrowDownwardIcon className={classes.scrollArrow} />
        </div>
      </section>
    );
}